import { checkSchema, MAX_PRODUCTS, normalizeText, normalizeUnit } from './schema';

export function validateProductTransfer(file) {
  const errors = checkSchema(file);
  if (errors.length) return { valid: false, errors };
  if (!file.products.length) errors.push('Selecciona al menos un producto');
  if (!/^\d{4}-\d{2}-\d{2}T/.test(file.exportedAt) || !Number.isFinite(Date.parse(file.exportedAt))) errors.push('exportedAt inválido');
  const nodes = [...file.products, ...file.dependencies.products];
  if (nodes.length > MAX_PRODUCTS) errors.push(`Máximo ${MAX_PRODUCTS} productos por archivo`);
  const allRefs = new Set(), sourceIds = new Set();
  const byRef = new Map(nodes.map((node) => [node.ref, node]));
  const selectors = new Map(file.dependencies.selectors.map((s) => [s.ref, s]));
  const graph = new Map(nodes.map((n) => [n.ref, new Set()]));
  const usedSelectors = new Set();
  [...nodes, ...file.dependencies.selectors].forEach((node) => {
    if (allRefs.has(node.ref)) errors.push(`Referencia duplicada: ${node.ref}`);
    allRefs.add(node.ref);
  });
  nodes.forEach((node) => {
    if (!node.sourceProductId.trim() || node.sourceProductId !== node.sourceProductId.trim() || node.sourceProductId.includes('/')) errors.push(`${node.ref}: sourceProductId inválido`);
    if (sourceIds.has(node.sourceProductId)) errors.push(`Producto de origen duplicado: ${node.sourceProductId}`);
    sourceIds.add(node.sourceProductId);
    const d = node.data, c = d.config || {};
    const error = (message) => errors.push(`${d.name} (${node.ref}): ${message}`);
    const edge = (ref) => {
      if (!byRef.has(ref)) error(`referencia inexistente ${ref}`);
      else graph.get(node.ref).add(ref);
      return byRef.get(ref)?.data;
    };
    if (d.type === 'insumo' && !d.unit) error('el insumo necesita unidad');
    if (d.type !== 'insumo' && d.unit !== '') error('unidad no soportada para producto final');
    if (d.yieldConfig?.enabled && (d.type !== 'insumo' || d.unit !== 'g')) error('rendimiento incompatible con tipo/unidad');
    if (d.type === 'insumo' && d.price !== 0) error('el precio de venta de un insumo debe ser cero');
    const baseItems = c.recipe?.baseItems || [];
    [...baseItems, ...(c.extraItems || [])].forEach((item) => {
      const target = edge(item.productRef);
      if (target && normalizeUnit(target.unit) !== item.unit) error(`unidad incompatible en ${item.name}`);
    });
    if (c.baseInsumoRef) {
      const target = edge(c.baseInsumoRef);
      if (target && (target.type !== 'insumo' || target.unit !== 'unidad')) error('base de helado incompatible');
    }
    if (c.promotion) {
      const target = edge(c.promotion.sourceProductRef);
      if (target && (!target.config?.recipe || target.config?.promotion?.enabled)) error('la promoción necesita un producto base con receta');
      if (!c.promotion.weekdays.length || new Set(c.promotion.weekdays).size !== c.promotion.weekdays.length) error('días de promoción inválidos');
      if (c.promotion.enabled && c.recipe) error('no se permite una receta temporal expandida en la promoción');
    }
    const groupIds = new Set();
    (c.recipe?.choiceGroups || []).forEach((group) => {
      if (groupIds.has(group.id)) error(`grupo repetido ${group.id}`);
      groupIds.add(group.id);
      if (group.min > group.max) error('min no puede superar max');
      const selector = selectors.get(group.selectorRef);
      if (!selector) { error(`selector inexistente ${group.selectorRef}`); return; }
      usedSelectors.add(selector.ref);
      if (selector.unit !== group.unit) error('unidad de selector incompatible');
      if (group.min > 0 && !selector.memberRefs.length) error(`categoría obligatoria sin miembros: ${selector.sourceCategory}`);
      selector.memberRefs.forEach(edge);
      if (group.defaultProductRef) {
        edge(group.defaultProductRef);
        if (!selector.memberRefs.includes(group.defaultProductRef)) error('la opción predeterminada no pertenece al selector');
      }
    });
    if (c.recipe && !baseItems.length && !(c.recipe.choiceGroups || []).length) error('receta vacía');
  });
  file.dependencies.selectors.forEach((s) => {
    if (!usedSelectors.has(s.ref)) errors.push(`Selector sin uso: ${s.ref}`);
    if (new Set(s.memberRefs).size !== s.memberRefs.length) errors.push(`Miembros duplicados: ${s.ref}`);
    s.memberRefs.forEach((ref) => {
      const target = byRef.get(ref)?.data;
      if (!target || target.type !== 'insumo' || target.unit !== s.unit || normalizeText(target.category) !== normalizeText(s.sourceCategory)) errors.push(`${s.ref}: miembro inválido ${ref}`);
    });
  });
  // Kahn's algorithm: bounded, iterative; a cycle must never be silently truncated.
  const indegree = new Map(nodes.map((node) => [node.ref, 0]));
  graph.forEach((edges) => edges.forEach((ref) => indegree.set(ref, indegree.get(ref) + 1)));
  const queue = [...indegree].filter(([, count]) => count === 0).map(([ref]) => ref);
  let processed = 0;
  for (let i = 0; i < queue.length; i += 1) {
    processed += 1;
    graph.get(queue[i]).forEach((ref) => { indegree.set(ref, indegree.get(ref) - 1); if (indegree.get(ref) === 0) queue.push(ref); });
  }
  if (processed !== nodes.length) errors.push('Ciclo de dependencias: revisa las recetas antes de exportar');
  const reached = new Set(), pending = file.products.map((n) => n.ref);
  while (pending.length) {
    const ref = pending.pop();
    if (reached.has(ref)) continue;
    reached.add(ref);
    (graph.get(ref) || []).forEach((id) => pending.push(id));
  }
  if (nodes.some((node) => !reached.has(node.ref))) errors.push('El archivo contiene dependencias desconectadas');
  return { valid: errors.length === 0, errors };
}
